import type { Suggestion } from '../../aiSuggestionsPluginType';
import type { SuggestionAvatarProfile } from '../components/SuggestionAvatar';

/**
 * Builds the deduped avatar list for a block's suggestions. Every suggestion is Rovo-created, so
 * always contributes an agent-slot avatar — the specific agent if `createdByAgent` is set,
 * otherwise the generic Rovo logo (a `profile`-less agent slot) — plus a user-slot avatar if
 * `createdByUser` is set. Profiles are deduped by account id (falling back to a fixed key for the
 * generic Rovo slot) so repeated agents/users collapse into a single stack entry.
 */
export function getUniqueAvatarProfiles(
	suggestionIds: Set<string> | undefined,
	suggestionById: Map<string, Suggestion> | undefined,
): SuggestionAvatarProfile[] {
	const profilesByKey = new Map<string, SuggestionAvatarProfile>();

	for (const suggestionId of suggestionIds ?? []) {
		const suggestion = suggestionById?.get(suggestionId);
		if (!suggestion) {
			continue;
		}

		const agentKey = suggestion.createdByAgent?.accountId ?? 'rovo-fallback';
		if (!profilesByKey.has(agentKey)) {
			profilesByKey.set(
				agentKey,
				suggestion.createdByAgent
					? { isAgent: true, profile: suggestion.createdByAgent }
					: { isAgent: true },
			);
		}

		if (suggestion.createdByUser && !profilesByKey.has(suggestion.createdByUser.accountId)) {
			profilesByKey.set(suggestion.createdByUser.accountId, {
				isAgent: false,
				profile: suggestion.createdByUser,
			});
		}
	}

	return Array.from(profilesByKey.values());
}
